import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetTenantQueryKey,
  TenantDTO,
  useTenantUpdate,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { FormElement } from '../FormElement';
import { FormSection } from '../FormSection';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { InputText } from '../Inputs/InputText';
import { InputNumber } from '../Inputs/InputNumber';
import { FormSubmitActions } from '../FormSubmitActions';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';

interface TenantSettingsConfigFormProps {
  tenant: TenantDTO;
}

export const TenantSettingsConfigForm = ({
  tenant,
}: TenantSettingsConfigFormProps) => {
  const { formatMessage } = useIntl();
  const tenantUpdate = useTenantUpdate();
  const queryClient = useQueryClient();
  const canEdit = checkUserPermissions({ permission: 'tenant:editor' });
  const [ref] = useAutoAnimateConditional<HTMLFormElement>();

  const [editMode, setEditMode] = useState(false);

  const startEdit = useCallback(() => {
    setEditMode(true);
  }, []);

  const schema = useMemo(
    () =>
      z.object({
        areff_card_type: z
          .string()
          .min(1, {
            message: formatMessage({
              defaultMessage: 'Får inte vara tomt',
            }),
          })
          .max(100, {
            message: formatMessage({
              defaultMessage: 'Måste vara max 100 tecken',
            }),
          }),
        areff_bearer_id: z
          .string()
          .min(1, {
            message: formatMessage({
              defaultMessage: 'Får inte vara tomt',
            }),
          })
          .max(100, {
            message: formatMessage({
              defaultMessage: 'Måste vara max 100 tecken',
            }),
          }),
        anonymization_interval_days: z.coerce.number().min(
          60,
          formatMessage({
            defaultMessage: 'Måste vara minst 60 dagar',
          }),
        ),
      }),
    [formatMessage],
  );

  type FormData = z.infer<typeof schema>;

  const methods = useForm<FormData>({
    defaultValues: {
      areff_card_type: tenant.areff_card_type ?? '',
      areff_bearer_id: tenant.areff_bearer_id ?? '',
      anonymization_interval_days: tenant.anonymization_interval_days ?? 60,
    },
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const { handleSubmit, reset, watch } = methods;

  const cancelEdit = useCallback(() => {
    reset();
    setEditMode(false);
  }, [reset]);

  const watchedValues = watch();

  const hasNoChanges = useMemo(() => {
    return (
      tenant.areff_card_type === watchedValues.areff_card_type &&
      tenant.areff_bearer_id === watchedValues.areff_bearer_id &&
      tenant.anonymization_interval_days ===
        watchedValues.anonymization_interval_days
    );
  }, [tenant, watchedValues]);

  const onSubmit = useCallback(
    (data: FormData) => {
      tenantUpdate.mutate(
        {
          data: {
            areff_card_type: data.areff_card_type,
            areff_bearer_id: data.areff_bearer_id,
            anonymization_interval_days: data.anonymization_interval_days,
          },
        },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage({
                  defaultMessage: 'Konfigurationen uppdaterades',
                }),
              },
              { autoClose: 3000 },
            );
            await queryClient.invalidateQueries({
              queryKey: getGetTenantQueryKey(),
            });
            reset(data);
            setEditMode(false);
          },
          onError: () => {
            toast.error(
              {
                title: formatMessage({
                  defaultMessage: 'Ett fel inträffade',
                }),
                description: formatMessage({
                  defaultMessage:
                    'Försök spara igen senare, eller vänligen kontakta support.',
                }),
              },
              { autoClose: 3000 },
            );
          },
        },
      );
    },
    [tenantUpdate, queryClient, formatMessage, reset],
  );

  return (
    <FormProvider {...methods}>
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-4"
        aria-label={formatMessage({
          defaultMessage: 'Konfigurationsinställningar',
        })}
      >
        <FormSection
          title={<FormattedMessage defaultMessage="Areff" />}
          onClickButton={startEdit}
          editMode={editMode}
          canSeeButton={canEdit}
        >
          <FormElement
            id="areff_card_type"
            label={<FormattedMessage defaultMessage="Korttyp" />}
            input={
              <InputText
                name="areff_card_type"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.areff_card_type}
                helperTextReserveSpace
              />
            }
          />
          <FormElement
            id="areff_bearer_id"
            label={<FormattedMessage defaultMessage="Bärar-ID" />}
            input={
              <InputText
                name="areff_bearer_id"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.areff_bearer_id}
                helperTextReserveSpace
              />
            }
          />
        </FormSection>
        <FormSection
          title={<FormattedMessage defaultMessage="Anonymisering" />}
        >
          <FormElement
            id="anonymization_interval_days"
            label={
              <FormattedMessage defaultMessage="Anonymiseringsfördröjning (dagar)" />
            }
            input={
              <InputNumber
                name="anonymization_interval_days"
                control={methods.control}
                editMode={editMode}
                error={methods.formState.errors.anonymization_interval_days}
                aria-describedby="anonymization-description"
              />
            }
          />
          <p id="anonymization-description" className="text-text-700">
            <FormattedMessage defaultMessage="Systemet tar automatiskt bort och anonymiserar data för barn och ungdomar (per elevkategori om de är med i flera) när de varit inaktiva i så här många dagar." />
          </p>
        </FormSection>
        {editMode && (
          <FormSubmitActions
            active={true}
            createMode={false}
            onCancel={cancelEdit}
            isLoading={tenantUpdate.isPending}
            disabled={hasNoChanges}
          />
        )}
      </form>
    </FormProvider>
  );
};
